import { canonicalizeSubmittedSource, } from './source-canonicalization.js';
/**
 * The presentation language of everything Mouja *generates* for a merchant
 * in Phase 1: the Business Profile narrative, market and audience
 * descriptions, findings and recommendations.
 *
 * It is the language the merchant selected in Mouja's interface when the
 * intake was created, captured once and persisted with the analysis run. It
 * is deliberately not the language of the retrieved evidence (which stays as
 * written), not the languages the business itself uses (`business.languages`,
 * which is evidence), and not the language of any video Mouja later produces
 * (the creative brief's own field). Conflating any two of those is the bug
 * this type exists to prevent.
 */
export const OUTPUT_LOCALES = ['en', 'ar'];
/**
 * What a caller that sends no locale gets. Older clients predate the field
 * and were always shown English, so the default keeps them unchanged.
 */
export const DEFAULT_OUTPUT_LOCALE = 'en';
export class OutputLocaleError extends Error {
    constructor(message) {
        super(message);
        this.name = 'OutputLocaleError';
    }
}
export function isOutputLocale(value) {
    return (typeof value === 'string' && OUTPUT_LOCALES.includes(value));
}
/**
 * Strict: exactly `"en"` or `"ar"`. Absent (undefined or null) means the
 * compatibility default; anything else — another tag, a region suffix,
 * different casing, a non-string — is refused rather than guessed.
 */
export function parseOutputLocale(raw) {
    if (raw === undefined || raw === null)
        return DEFAULT_OUTPUT_LOCALE;
    if (isOutputLocale(raw))
        return raw;
    throw new OutputLocaleError('outputLocale must be "en" or "ar".');
}
/** The human name a prompt uses; never a locale tag a model may misread. */
export function outputLocaleLanguageName(locale) {
    return locale === 'ar' ? 'Arabic' : 'English';
}
const INSTAGRAM_BUSINESS_RESERVED = new Set([
    'about',
    'accounts',
    'ads',
    'api',
    'challenge',
    'developer',
    'developers',
    'direct',
    'directory',
    'emails',
    'explore',
    'legal',
    'oauth',
    'privacy',
    'terms',
    'web',
    'stories',
    'shop',
    'shopping',
    'store',
    'stores',
    'products',
    'product',
    'checkout',
    'share',
    'p',
    'reel',
    'reels',
    'tv',
]);
/** New entry policy only; historical canonicalization and hashes remain intact. */
export function classifyInstagramBusinessUrl(raw) {
    if (typeof raw !== 'string' || /[\\\p{Cc}]/u.test(raw.trim()))
        return null;
    const originalPath = raw.trim().split(/[?#]/u)[0] ?? '';
    if (originalPath.includes('%') || /\/\.{1,2}(?:\/|$)/u.test(originalPath))
        return null;
    let source;
    try {
        source = canonicalizeMerchantSourceUrl(raw);
    }
    catch {
        return null;
    }
    const url = new URL(source.canonicalUrl);
    if (!['instagram.com', 'www.instagram.com', 'm.instagram.com'].includes(url.hostname) ||
        url.port !== '' ||
        /[%\\]/u.test(url.pathname) ||
        url.pathname.includes('//'))
        return null;
    const parts = url.pathname.split('/').filter(Boolean);
    const first = parts[0] ?? '';
    const isHandle = (value) => /^[a-z0-9_](?:[a-z0-9._]{0,28}[a-z0-9_])?$/iu.test(value) &&
        !value.includes('..') &&
        !INSTAGRAM_BUSINESS_RESERVED.has(value.toLowerCase());
    const isCode = (value) => /^[A-Za-z0-9_-]{1,128}$/u.test(value);
    let kind;
    let handle = null;
    let shortcode = null;
    if (parts.length === 1 && isHandle(first)) {
        kind = 'PROFILE';
        handle = first.toLowerCase();
    }
    else if ((parts.length === 2 &&
        first === 'share' &&
        parts[1] !== 'reel' &&
        isCode(parts[1] ?? '')) ||
        (parts.length === 3 &&
            first === 'share' &&
            parts[1] === 'reel' &&
            isCode(parts[2] ?? ''))) {
        kind = 'SHARE';
    }
    else {
        if (parts.length !== 2 ||
            !['p', 'reel', 'reels'].includes(first) ||
            !isCode(parts[1] ?? ''))
            return null;
        kind = first === 'p' ? 'POST' : 'REEL';
        shortcode = parts[1] ?? null;
    }
    url.protocol = 'https:';
    url.hostname = 'www.instagram.com';
    url.pathname =
        kind === 'PROFILE'
            ? `/${handle}/`
            : kind === 'REEL'
                ? `/reel/${shortcode}/`
                : `/${parts.join('/')}/`;
    return {
        version: 'instagram-business-entry.v1',
        kind,
        canonicalUrl: url.toString(),
        handle,
        shortcode,
    };
}
/** Enable only after the social backend is composed; website inputs use v1 verbatim. */
export function parseMerchantOwnedSources(value, options = {}) {
    const entry = Array.isArray(value) && value.length === 1 ? sourceObject(value[0]) : null;
    if (entry?.['platform'] !== 'instagram' || !options.instagramBusinessEnabled) {
        return parseWebsiteMerchantOwnedSources(value);
    }
    if (Object.keys(entry).length !== 3 ||
        Object.keys(entry).some((key) => !['url', 'platform', 'ownership'].includes(key)))
        throw new MerchantSourceUrlError('Each source must contain only url, platform, and ownership.');
    if (entry['ownership'] !== 'mine') {
        throw new MerchantSourceUrlError('Business Profile sources must be yours.');
    }
    const instagramBusinessSource = classifyInstagramBusinessUrl(entry['url']);
    if (instagramBusinessSource === null) {
        throw new MerchantSourceUrlError('Use a public Instagram profile, post, Reel, or carousel link. Shop, login, and other platform links are not supported.');
    }
    return [
        {
            ...canonicalizeMerchantSourceUrl(entry['url']),
            submittedPlatform: 'instagram',
            ownership: 'mine',
            instagramBusinessSource,
        },
    ];
}
export class MerchantSourceUrlError extends Error {
    constructor(message) {
        super(message);
        this.name = 'MerchantSourceUrlError';
    }
}
function sourceObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
        ? value
        : null;
}
function submittedPlatform(value) {
    return value === 'website' || value === 'instagram' || value === 'tiktok'
        ? value
        : null;
}
/**
 * Validate the demo intake source contract at a trusted boundary.
 *
 * A Production Line begins with the merchant's public website. One public
 * Instagram or TikTok profile may also be supplied, but it is enrichment and
 * must never block the landing-page journey. Deep website paths are retained
 * by canonicalization as intent evidence; social media posts are deliberately
 * refused because they do not establish account ownership.
 */
function parseWebsiteMerchantOwnedSources(value) {
    if (!Array.isArray(value) || value.length < 1 || value.length > 2) {
        throw new MerchantSourceUrlError('Provide one business website and, optionally, one Instagram or TikTok profile.');
    }
    const sources = value.map((entry) => {
        const source = sourceObject(entry);
        if (source === null) {
            throw new MerchantSourceUrlError('Each source must identify its URL, platform, and owned relationship.');
        }
        const keys = Object.keys(source);
        if (keys.some((key) => !['url', 'platform', 'ownership'].includes(key)) ||
            keys.length !== 3) {
            throw new MerchantSourceUrlError('Each source must contain only url, platform, and ownership.');
        }
        const platform = submittedPlatform(source['platform']);
        if (platform === null) {
            throw new MerchantSourceUrlError('The social source must be an Instagram or TikTok profile.');
        }
        if (source['ownership'] !== 'mine') {
            throw new MerchantSourceUrlError('Business Profile sources must be yours.');
        }
        const canonicalized = canonicalizeMerchantSourceUrl(source['url']);
        if (canonicalized.platform !== platform.toUpperCase()) {
            throw new MerchantSourceUrlError(`The ${platform} source does not match its URL.`);
        }
        if (platform !== 'website' &&
            (canonicalized.canonical.pageKind !== 'SOCIAL_PROFILE' ||
                canonicalized.externalIdentity === null)) {
            throw new MerchantSourceUrlError(`Use a public ${platform === 'instagram' ? 'Instagram' : 'TikTok'} profile URL, not a post, Reel, video, short link, or platform homepage.`);
        }
        return {
            ...canonicalized,
            submittedPlatform: platform,
            ownership: 'mine',
        };
    });
    if (new Set(sources.map((source) => source.canonicalUrl)).size !== sources.length) {
        throw new MerchantSourceUrlError('Business Profile sources must be different.');
    }
    const websites = sources.filter((source) => source.platform === 'WEBSITE');
    const socials = sources.filter((source) => source.platform === 'INSTAGRAM' || source.platform === 'TIKTOK');
    if (websites.length !== 1 || socials.length > 1) {
        throw new MerchantSourceUrlError('Provide exactly one website and, optionally, one Instagram or TikTok profile.');
    }
    return sources;
}
/** The contract's bound on a stored source URL. */
const MIN_SOURCE_URL_LENGTH = 8;
const MAX_SOURCE_URL_LENGTH = 2048;
/**
 * Canonicalize a merchant URL at the trusted server boundary.
 *
 * The browser may draw a useful chip, but its classification is never
 * accepted as authority: the platform, the cleaned intent URL and the
 * business root are all derived again here from the URL itself, by the one
 * shared canonicalizer in `source-canonicalization.ts`. This function owns
 * only the intake-specific envelope — length bounds and the error type the
 * HTTP layer turns into a 400.
 */
export function canonicalizeMerchantSourceUrl(raw) {
    if (typeof raw !== 'string' ||
        raw.trim().length < MIN_SOURCE_URL_LENGTH ||
        raw.length > MAX_SOURCE_URL_LENGTH) {
        throw new MerchantSourceUrlError('Each source must be a valid public URL.');
    }
    const result = canonicalizeSubmittedSource(raw);
    if (!result.ok) {
        switch (result.refusal) {
            case 'EMBEDDED_CREDENTIALS':
                throw new MerchantSourceUrlError('Sources must use HTTP(S) and cannot contain embedded credentials.');
            case 'UNSUPPORTED_SCHEME':
                throw new MerchantSourceUrlError('Each source must be an absolute HTTP(S) URL.');
            case 'INVALID_HOST':
                throw new MerchantSourceUrlError('Each source must have a public host.');
            default:
                throw new MerchantSourceUrlError('Each source must be an absolute HTTP(S) URL.');
        }
    }
    const canonical = result.source;
    if (canonical.cleanedIntentUrl.length > MAX_SOURCE_URL_LENGTH) {
        throw new MerchantSourceUrlError('Each source must be a valid public URL.');
    }
    return {
        platform: canonical.sourceKind,
        // The stored canonical URL is the cleaned intent: the page the merchant
        // meant, without campaign parameters. The exact submitted spelling is
        // preserved separately on `canonical.originalUrl` for audit.
        canonicalUrl: canonical.cleanedIntentUrl,
        externalIdentity: canonical.handle,
        canonical,
    };
}
/**
 * Backward-compatible classification for callers that only need the platform
 * triple. New code should prefer {@link canonicalizeMerchantSourceUrl}, which
 * additionally carries the business root, page kind and audit fields.
 */
export function classifyMerchantSourceUrl(raw) {
    const { platform, canonicalUrl, externalIdentity } = canonicalizeMerchantSourceUrl(raw);
    return { platform, canonicalUrl, externalIdentity };
}
//# sourceMappingURL=merchant-intake.js.map